"use client";

import { type ReactNode, type RefObject, useEffect, useRef } from "react";

import { api } from "@/lib/trpc/react";

// Copying a phrase isn't copying the listing.
const MIN_COPIED_CHARACTERS = 200;

function selectionInside(container: HTMLElement | null): Selection | null {
  const selection = document.getSelection();
  if (
    !container ||
    !selection ||
    selection.rangeCount === 0 ||
    selection.isCollapsed
  ) {
    return null;
  }
  return selection.getRangeAt(0).intersectsNode(container) ? selection : null;
}

function CopyListener({
  containerRef,
  listingToken,
}: {
  containerRef: RefObject<HTMLElement | null>;
  listingToken: string;
}): null {
  const { data: sentence } = api.opportunityListing.copySentence.useQuery(
    { token: listingToken },
    { staleTime: Infinity },
  );

  useEffect(() => {
    if (!sentence) {
      return;
    }
    const onCopy = (event: ClipboardEvent) => {
      const copied =
        selectionInside(containerRef.current)?.toString().trim() ?? "";
      if (
        !event.clipboardData ||
        event.defaultPrevented ||
        copied.length < MIN_COPIED_CHARACTERS
      ) {
        return;
      }
      event.clipboardData.setData("text/plain", `${copied}\n\n${sentence}`);
      event.preventDefault();
    };
    document.addEventListener("copy", onCopy);
    return () => {
      document.removeEventListener("copy", onCopy);
    };
  }, [containerRef, sentence]);

  return null;
}

/**
 * A listing description whose copies carry the copied-listing check's
 * sentence (`services/copy-check` in @repo/api). The sentence exists only in
 * the clipboard, never in the page or its HTML. It is fetched as the listing
 * loads, so even a select-all and copy the moment the page opens carries it.
 * Without a token, as on previews, this is a plain wrapper.
 */
export function ListingCopyCheck({
  listingToken,
  className,
  children,
}: {
  listingToken: string | null | undefined;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div ref={ref} className={className}>
      {children}
      {listingToken && (
        <CopyListener containerRef={ref} listingToken={listingToken} />
      )}
    </div>
  );
}
